/**
 * Premium empty-state primitive — used on any surface that would otherwise
 * greet the operator with a blank card.
 *
 * The rule: an empty state must always answer three questions —
 *   1. What is this thing?
 *   2. Why does it matter?
 *   3. What's the single next action?
 *
 * Compose:
 *   <PremiumEmpty
 *     eyebrow="Publishing"
 *     title="Connect a channel to unlock scheduled publishing."
 *     body="Once a platform is connected, every future asset ships through the same secure vault — no re-authentication per campaign."
 *     bullets={["OAuth-encrypted at rest", "Retries + dead-letter monitoring", "One-click cancel"]}
 *     primary={{ label: "Connect a channel", to: "/publishing" }}
 *     secondary={{ label: "Read the guide", to: "/academy" }}
 *     icon={<Radio className="h-5 w-5" />}
 *   />
 */
import { CheckCircle2 } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface PremiumEmptyCTA {
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  testId?: string;
}

export interface PremiumEmptyProps {
  eyebrow?: string;
  title: string;
  /** Primary explanation body. Prefer this for new call-sites. */
  body?: string;
  /**
   * Alias for `body` kept for consumer convenience. When both are supplied,
   * `body` wins. Semantics are identical.
   */
  description?: string;
  bullets?: string[];
  primary?: PremiumEmptyCTA;
  secondary?: PremiumEmptyCTA;
  icon?: ReactNode;
  testId?: string;
}

function CTA({ cta, primary }: { cta: PremiumEmptyCTA; primary?: boolean }): JSX.Element {
  const cls = primary ? "md-btn md-btn--primary" : "md-btn md-btn--ghost";
  const style = { padding: "12px 22px", fontSize: 14 };
  if (cta.to) {
    return <Link to={cta.to} data-testid={cta.testId} className={cls} style={style}>{cta.label}</Link>;
  }
  if (cta.href) {
    return <a href={cta.href} target="_blank" rel="noopener noreferrer" data-testid={cta.testId} className={cls} style={style}>{cta.label}</a>;
  }
  return <button type="button" onClick={cta.onClick} data-testid={cta.testId} className={cls} style={style}>{cta.label}</button>;
}

export function PremiumEmpty({
  eyebrow, title, body, description, bullets, primary, secondary, icon, testId,
}: PremiumEmptyProps): JSX.Element {
  const resolvedBody = body ?? description;
  return (
    <div
      data-testid={testId ?? "premium-empty"}
      className="mx-auto max-w-2xl rounded-2xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)] p-8 text-center md:p-10"
      style={{ boxShadow: "0 40px 100px -50px rgba(0,0,0,0.55)" }}
    >
      {icon && (
        <div className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-2xl border border-[color:var(--md-amber)]/30 bg-[var(--md-amber)]/10 text-[var(--md-amber)]">
          {icon}
        </div>
      )}
      {eyebrow && <div className="md-eyebrow md-eyebrow--amber">{eyebrow}</div>}
      <h2 className="mt-3 font-display italic tracking-tight text-2xl leading-[1.15] text-[var(--md-ivory-0)] md:text-3xl">
        {title}
      </h2>
      {resolvedBody && (
        <p className="mx-auto mt-4 max-w-lg text-[14px] leading-relaxed text-[var(--md-fog)]">
          {resolvedBody}
        </p>
      )}
      {bullets && bullets.length > 0 && (
        <ul className="mx-auto mt-5 flex max-w-md flex-col items-start gap-2 text-left text-[13px] text-[var(--md-ivory-0)]">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--md-good)]" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {(primary || secondary) && (
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          {primary && <CTA cta={primary} primary />}
          {secondary && <CTA cta={secondary} />}
        </div>
      )}
    </div>
  );
}
